import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';

import { SuperadminAuthGuard } from './core/auth/guards/superadminAuth.guard';

// The default value to config data unless overwritten
const defaultData = {
    group_list: false
}

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path directly to sign-in for testing
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'sign-in'
    },

    // Redirect signed-in user to the '/overview'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'main' },
    { path: 'signed-in-redirect-overview', pathMatch: 'full', redirectTo: 'overview' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            ...defaultData,
            layout: 'empty'
        },
        children: [
            { path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes') },
            { path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes') },
            { path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes') },
            { path: 'force-reset-password', loadChildren: () => import('app/modules/auth/force-reset-password/force-reset-password.routes') },
            { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') },
            { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes') },
            { path: 'sign-up-confirmation', loadChildren: () => import('app/modules/auth/sign-up-confirmation/sign-up-confirmation.routes') },
        ]
    },

    // Tos and Privacy Policy routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            ...defaultData,
            layout: 'empty'
        },
        children: [
            { path: 'terms-of-service', loadChildren: () => import('app/modules/auth/terms-of-service/terms-of-service.routes') },
            { path: 'privacy-policy', loadChildren: () => import('app/modules/auth/privacy-policy/privacy-policy.routes') }
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        component: LayoutComponent,
        data: {
            ...defaultData,
            layout: 'empty'
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
            { path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes') }
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            ...defaultData,
            layout: 'dense',
        },
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            { 
                path: 'main', 
                loadChildren: () => import('app/modules/admin/main-page/main-page.routes'),  
            },
            { 
                path: 'overview', 
                loadChildren: () => import('app/modules/admin/overview-page/overview-page.routes'),
                data: { group_list: true } 
            },
            { 
                path: 'devices', 
                loadChildren: () => import('app/modules/admin/devices-page/devices-page.routes'),
                data: { group_list: true }  
            },
            { 
                path: 'alertrules', 
                loadChildren: () => import('app/modules/admin/alert-rules-page/alert-rules-page.routes'),
                data: { group_list: true }  
            },
            { 
                path: 'members', 
                loadChildren: () => import('app/modules/admin/members-page/members-page.routes'),
                data: { group_list: true }  
            },
            { 
                path: 'devices/:group_id/:id', 
                loadChildren: () => import('app/modules/admin/device-detail/device-detail.routes'),   
            },
            { 
                path: 'log-meter/:group_id/:id', 
                loadChildren: () => import('app/modules/admin/log-meter/log-meter.routes'),   
            },
            { 
                path: 'profile', 
                loadChildren: () => import('app/modules/user-profile/user-profile.routes'),   
            },
            { 
                path: 'price', 
                loadChildren: () => import('app/modules/admin/master-price-page/master-price-page.routes'),  
            },
            { 
                path: 'meter-capacity', 
                loadChildren: () => import('app/modules/admin/master-meter-type-page/master-meter-type-page.routes'),   
            },
            { 
                path: 'monitoring', 
                loadChildren: () => import('app/modules/admin/monitoring-dashboard/monitoring-dashboard.routes'), 
            },
        ]
    },

    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            ...defaultData,
            layout: 'dense'
        },
    },

    // Super Admin routes
    {
        path: '',
        canActivate: [SuperadminAuthGuard],
        canActivateChild: [SuperadminAuthGuard],
        component: LayoutComponent,
        data: {
            ...defaultData,
            layout: 'dense'
        },
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            { path: 'superadmin/home', loadChildren: () => import('app/modules/superadmin/home/home.routes') },
            { path: 'superadmin/products', loadChildren: () => import('app/modules/superadmin/products/products.routes') },
            { path: 'superadmin/solutions', loadChildren: () => import('app/modules/superadmin/solutions/solutions.routes') },
            { path: 'superadmin/meter-configuration', loadChildren: () => import('./modules/superadmin/meter-configuration/generate-token.routes') },
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
        ]
    }
];
